import React, { useEffect, useState } from "react";
import { api } from "../../app/Api/ApiSlice";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import Loader from "../../components/Loader";

const OrgSettings = () => {
    const { organization } = useParams();

    // Loading states
    const [isLoading, setIsLoading] = useState(false);
    const [savingLogo, setSavingLogo] = useState(false);
    const [savingDetails, setSavingDetails] = useState(false);
    const [savingAddress, setSavingAddress] = useState(false);
    const [savingContacts, setSavingContacts] = useState(false);

    // Profile
    const [name, setName] = useState("");
    const [tagline, setTagline] = useState("");
    const [description, setDescription] = useState("");
    const [category, setCategory] = useState("business");

    // Address
    const [country, setCountry] = useState("");
    const [state, setState] = useState("");
    const [city, setCity] = useState("");
    const [street, setStreet] = useState("");
    const [building, setBuilding] = useState("");
    const [room, setRoom] = useState("");
    const [postalCode, setPostalCode] = useState("");

    // Contacts
    const [email, setEmail] = useState("");
    const [website, setWebsite] = useState("");
    const [phone, setPhone] = useState("");
    const [phone2, setPhone2] = useState("");

    // Media
    const [logo, setLogo] = useState(null);
    const [logoPreview, setLogoPreview] = useState("");

    // delete
    const [typed, setTyped] = useState("")
    const [deleting, setDeleting] = useState(false)

    // Fetch org data
    useEffect(() => {
        const getOrg = async () => {
            try {
                setIsLoading(true);
                const { data } = await api.get(`/${organization}`);

                console.log(data)

                setName(data.org?.name || "");
                setTagline(data.org?.tagline || "");
                setDescription(data.org?.description || "");
                setCategory(data.org?.category || "business");

                setCountry(data.org?.address?.country || "");
                setState(data.org?.address?.state || "");
                setCity(data.org?.address?.city || "");
                setStreet(data.org?.address?.street || "");
                setBuilding(data.org?.address?.building || "");
                setRoom(data.org?.address?.room || "");
                setPostalCode(data.org?.address?.postalCode || "");

                setEmail(data.org?.contacts?.email || "");
                setWebsite(data.org?.contacts?.website || "");
                setPhone(data.org?.contacts?.phone || "");
                setPhone2(data.org?.contacts?.phone2 || "");
                setLogoPreview(data.org?.contacts?.logo || "");
            } catch (err) {
                toast.error(err?.response?.data?.message || "Failed to load settings");
            } finally {
                setIsLoading(false);
            }
        };

        getOrg();
    }, [organization]);

    // Upload helper
    const uploadToS3 = async (file) => {
        const presignRes = await api.post("/uploads/presign", {
            filename: file.name,
            contentType: file.type,
        });

        const { uploadUrl, key } = presignRes.data;

        const res = await fetch(uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": file.type },
            body: file,
        });

        if (!res.ok) throw new Error("Upload failed");

        return {
            key,
            type: "image",
            mimeType: file.type,
            name: file.name,
            size: file.size,
        };
    };

    // Logo
    const handleLogoUpload = async (e) => {
        e.preventDefault();
        if (!logo) return;

        try {
            setSavingLogo(true);
            const uploadedFile = await uploadToS3(logo);

            await api.post(`/${organization}/manage/logo`, {
                file: uploadedFile,
            });

            toast.success("Logo updated");
        } catch (err) {
            console.log(err)
            toast.error(err?.message || "Upload failed");
        } finally {
            setSavingLogo(false);
        }
    };

    // Details
    const handleUpdateDetails = async (e) => {
        e.preventDefault();

        try {
            setSavingDetails(true);
            await api.put(`/${organization}/manage/details`, {
                name,
                tagline,
                description,
                category,
            });
            toast.success("Details updated");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Update failed");
        } finally {
            setSavingDetails(false);
        }
    };

    // Address
    const handleUpdateAddress = async (e) => {
        e.preventDefault();

        try {
            setSavingAddress(true);
            await api.put(`/${organization}/manage/address`, {
                country,
                state,
                city,
                street,
                building,
                room,
                postalCode,
            });
            toast.success("Address updated");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Update failed");
        } finally {
            setSavingAddress(false);
        }
    };

    // Contacts
    const handleUpdateContacts = async (e) => {
        e.preventDefault();

        try {
            setSavingContacts(true);
            await api.put(`/${organization}/manage/contacts`, {
                email,
                website,
                phone,
                phone2,
            });
            toast.success("Contacts updated");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Update failed");
        } finally {
            setSavingContacts(false);
        }
    };

    if (isLoading) {
        return (
            <div className="loader">
                <Loader />
            </div>
        );
    }

    // Delete account
    const handleDelete = async (e) => {
        e.preventDefault()
        window.confirm("delete this organization?")
        try {
            setDeleting(true);
            await api.delete(`/${organization}/manage`);
            toast.success("Account scheduled for deletion");
        } catch (err) {
            console.log(err)
            toast.error("Delete failed");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="settings-container">
            <header className="settings-header">
                <h1>Organization Settings</h1>
                <p>Manage {organization} public profile and contact details.</p>
            </header>

            {/* Branding */}
            <section className="settings-card">
                <h3>Branding</h3>
                <div className="logo-upload-section">
                    <div className="org-logo-preview">
                        {logoPreview ? (
                            <img src={logoPreview} alt="Logo" />
                        ) : (
                            <div className="placeholder">No Logo</div>
                        )}
                    </div>

                    <div className="upload-controls">
                        <input
                            type="file"
                            id="logo-input"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    setLogo(file);
                                    setLogoPreview(URL.createObjectURL(file));
                                }
                            }}
                        />

                        <button
                            className="btn-save"
                            onClick={handleLogoUpload}
                            disabled={savingLogo}
                        >
                            {savingLogo ? "Uploading..." : "Save Logo"}
                        </button>
                    </div>
                </div>
            </section>

            {/* Details */}
            <section className="settings-card">
                <h3>General Details</h3>
                <form onSubmit={handleUpdateDetails}>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Organization Name</label>
                            <input value={name} onChange={(e) => setName(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>Tagline</label>
                            <input value={tagline} onChange={(e) => setTagline(e.target.value)} />
                        </div>

                        <div className="form-group full-width">
                            <label>Description</label>
                            <textarea rows="3" value={description} onChange={(e) => setDescription(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>Category</label>
                            <select value={category} onChange={(e) => setCategory(e.target.value)}>
                                <option value="business">Business</option>
                                <option value="government">Government</option>
                                <option value="religious">Religious</option>
                                <option value="non-profit">Non-profit</option>
                            </select>
                        </div>
                    </div>

                    <button className="btn-save" type="submit" disabled={savingDetails}>
                        {savingDetails ? "Saving..." : "Update Details"}
                    </button>
                </form>
            </section>

            {/* Address */}
            <section className="settings-card">
                <h3>Address Information</h3>
                <form onSubmit={handleUpdateAddress}>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Country</label>
                            <input value={country} onChange={(e) => setCountry(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>State/Province</label>
                            <input value={state} onChange={(e) => setState(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>City</label>
                            <input value={city} onChange={(e) => setCity(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>Postal Code</label>
                            <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>Street</label>
                            <input value={street} onChange={(e) => setStreet(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>Building & Room</label>
                            <div className="inline-inputs">
                                <input value={building} onChange={(e) => setBuilding(e.target.value)} />
                                <input value={room} onChange={(e) => setRoom(e.target.value)} />
                            </div>
                        </div>
                    </div>

                    <button className="btn-save" type="submit" disabled={savingAddress}>
                        {savingAddress ? "Saving..." : "Update Address"}
                    </button>
                </form>
            </section>

            {/* Contacts */}
            <section className="settings-card">
                <h3>Contact Information</h3>
                <form onSubmit={handleUpdateContacts}>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Public Email</label>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>Website URL</label>
                            <input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>Primary Phone</label>
                            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>Secondary Phone</label>
                            <input type="tel" value={phone2} onChange={(e) => setPhone2(e.target.value)} />
                        </div>
                    </div>

                    <button className="btn-save" type="submit" disabled={savingContacts}>
                        {savingContacts ? "Saving..." : "Update Contacts"}
                    </button>
                </form>
            </section>
            <section className="settings-card">
                <h3>Delete</h3>
                <form onSubmit={handleDelete}>
                    <div className="form-grid">
                        <div className="form-group">
                            <input
                                type="text"
                                value={typed}
                                onChange={(e) => setTyped(e.target.value)}
                                placeholder="Type the word delete to continue"
                            />
                        </div>
                    </div>
                    <button className="btn-save" type="submit" disabled={typed !== "delete"}>
                        {deleting ? "please wait..." : "Delete"}
                    </button>
                </form>
            </section>
        </div>
    );
};

export default OrgSettings;
